import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Truck, CheckCircle2, Package, Boxes, Map as MapIcon, Plus, History, XCircle, Warehouse } from "lucide-react";
import api from "@/api";
import TopBar from "@/components/TopBar";
import { StatusPill } from "@/components/Pills";
import NoSaleDialog from "@/components/NoSaleDialog";
import AttendanceCard from "@/components/AttendanceCard";
import useGpsTracking from "@/hooks/useGpsTracking";
import { useLang } from "@/context/LanguageContext";

const Stat = ({ icon: Icon, label, value, color, testid, onClick }) => {
  const Wrapper = onClick ? "button" : "div";
  return (
    <Wrapper onClick={onClick} className={`stat-card text-left transition ${onClick ? "hover:shadow-lg active:scale-[0.99] cursor-pointer" : ""}`} data-testid={testid}>
      <div className="flex items-start justify-between mb-2">
        <div className={`w-10 h-10 rounded-xl ${color} grid place-items-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
      <div className="text-xs text-[#86868B] uppercase tracking-wider">{label}</div>
      <div className="heading-font text-2xl font-bold text-[#1D1D1F] mt-1">{value}</div>
    </Wrapper>
  );
};

export default function SalesmanHome() {
  const nav = useNavigate();
  const { t, displayName } = useLang();
  const [data, setData] = useState({ stats: {}, today_orders: [] });
  const [loading, setLoading] = useState(true);
  const [showNoSale, setShowNoSale] = useState(false);

  // Phase G: live GPS pings while logged-in (gate handles attendance check)
  useGpsTracking({ enabled: true });

  useEffect(() => {
    api.get("/dashboard/salesman").then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  const s = data.stats || {};

  return (
    <div className="min-h-screen bg-[#F5F5F7] pb-10">
      <TopBar title={t("dashboard")} />
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6 fade-up">
        <AttendanceCard />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => nav("/salesman/sales-history")}
              data-testid="open-sales-history-btn"
              className="bg-white hover:bg-[#0055FF] hover:text-white text-[#0055FF] border border-[#0055FF] rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-2 transition shadow-sm"
            >
              <History className="w-4 h-4" /> {t("my_direct_sales")}
            </button>
            <button
              onClick={() => nav("/salesman/stock")}
              data-testid="open-stock-btn"
              className="bg-white hover:bg-[#0055FF] hover:text-white text-[#0055FF] border border-[#0055FF] rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-2 transition shadow-sm"
            >
              <Warehouse className="w-4 h-4" /> {t("stock_balance")}
            </button>
            <button
              onClick={() => setShowNoSale(true)}
              data-testid="open-nosale-btn"
              className="bg-white hover:bg-[#FF3B30] hover:text-white text-[#FF3B30] border border-[#FF3B30] rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-2 transition shadow-sm"
            >
              <XCircle className="w-4 h-4" /> {t("not_bought")}
            </button>
          </div>
          <button
            onClick={() => nav("/salesman/map")}
            data-testid="open-map-btn"
            className="bg-white hover:bg-[#0055FF] hover:text-white text-[#0055FF] border border-[#0055FF] rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-2 transition shadow-sm"
          >
            <MapIcon className="w-4 h-4" /> {t("route_map")}
          </button>
        </div>

        {/* Big primary CTA: Direct Sale */}
        <button
          onClick={() => nav("/salesman/direct-sale")}
          data-testid="direct-sale-btn"
          className="w-full bg-gradient-to-r from-[#0055FF] to-[#3478FF] hover:from-[#0044CC] hover:to-[#1F60E6] text-white rounded-3xl py-5 text-xl font-bold flex items-center justify-center gap-3 shadow-lg active:scale-[0.99] transition"
        >
          <Plus className="w-7 h-7" /> {t("direct_sale")}
        </button>

        {/* Phase 13: UNIT-ONLY stats — Phase C: clickable drilldowns; Phase F: + Shop Visits */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat icon={Truck}        color="bg-[#0055FF]" label={t("todays_orders")}    value={data.today_orders?.length || 0} testid="stat-today" />
          <Stat icon={CheckCircle2} color="bg-[#34C759]" label={t("delivered_today")}  value={s.delivered || 0} testid="stat-delivered" onClick={() => nav("/salesman/drilldown/delivered")} />
          <Stat icon={Package}      color="bg-[#FF9500]" label={t("pending")}          value={s.pending || 0}   testid="stat-pending"   onClick={() => nav("/salesman/drilldown/pending")} />
          <Stat icon={Boxes}        color="bg-[#5856D6]" label={t("units_sold")}       value={s.units_sold || 0} testid="stat-units"     onClick={() => nav("/salesman/drilldown/boxes")} />
          <Stat icon={MapIcon}      color="bg-[#FF3B30]" label={t("shop_visits")}      value={s.shop_visits || 0} testid="stat-shop-visits" onClick={() => nav("/salesman/drilldown/shops")} />
        </div>

        {/* Phase C: Quick links to Total Shops + No-Sale list */}
        <div className="grid grid-cols-2 gap-3">
          <button
            data-testid="open-shops-btn"
            onClick={() => nav("/salesman/drilldown/shops")}
            className="bg-white rounded-2xl p-4 soft-shadow text-left hover:shadow-lg active:scale-[0.99] transition flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-xl bg-[#0055FF]/10 grid place-items-center"><MapIcon className="w-5 h-5 text-[#0055FF]" /></div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold">{t("total_shops")}</div>
              <div className="heading-font font-bold text-[#1D1D1F]">{t("total_shops")}</div>
            </div>
          </button>
          <button
            data-testid="open-nosale-list-btn"
            onClick={() => nav("/salesman/drilldown/nosale")}
            className="bg-white rounded-2xl p-4 soft-shadow text-left hover:shadow-lg active:scale-[0.99] transition flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-xl bg-[#FF3B30]/10 grid place-items-center"><XCircle className="w-5 h-5 text-[#FF3B30]" /></div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold">{t("not_bought")}</div>
              <div className="heading-font font-bold text-[#1D1D1F]">{t("no_sale")}</div>
            </div>
          </button>
        </div>

        <div>
          <h2 className="heading-font font-bold text-xl text-[#1D1D1F] mb-3">{t("todays_orders")}</h2>
          {loading ? <div className="text-center text-[#86868B] py-8">{t("loading")}</div> :
            data.today_orders?.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center soft-shadow">
                <Package className="w-12 h-12 mx-auto text-[#86868B] mb-2" />
                <div className="text-[#86868B]">{t("no_orders")}</div>
              </div>
            ) : (
              <div className="space-y-3">
                {data.today_orders?.map(o => (
                  <button
                    key={o.id}
                    onClick={() => nav(`/salesman/order/${o.id}`)}
                    data-testid={`order-card-${o.id}`}
                    className="w-full bg-white rounded-2xl p-5 soft-shadow text-left hover:shadow-lg transition border border-transparent hover:border-[#0055FF]/30"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="min-w-0">
                        <div className="heading-font font-bold text-lg text-[#1D1D1F] truncate">{o.shop_name}</div>
                        <div className="text-sm text-[#86868B]">{o.total_units} {t("units")} • {o.items?.length || 0} {t("items")}</div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <StatusPill status={o.status} t={t} />
                        {o.priority && <span className="pill bg-red-50 text-red-700">⚡ {t("high_priority")}</span>}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {(o.items || []).slice(0, 3).map((it, i) => (
                        <span key={i} className="pill bg-gray-100 text-gray-700 text-xs">{displayName(it)} ×{it.quantity}</span>
                      ))}
                      {o.items?.length > 3 && <span className="pill bg-gray-100 text-gray-700 text-xs">+{o.items.length - 3}</span>}
                    </div>
                  </button>
                ))}
              </div>
            )
          }
        </div>
      </div>
      <NoSaleDialog open={showNoSale} onClose={() => setShowNoSale(false)} />
    </div>
  );
}
